import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Button from '../UI/Button';
import Card from '../UI/Card';
import SpinPrizeFormModal from './SpinPrizeFormModal';
import spinService, { SpinPrizeItem } from '../../api/services/spin.service';
import { SpinSettings } from '../../api/services/platformSettings.service';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  GiftIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';

interface SpinTabProps {
  spin: SpinSettings;
  onSaveSpin: (spin: SpinSettings) => Promise<void>;
  saving: boolean;
}

const inputClass =
  'w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400';
const labelClass = 'block text-xs font-medium text-gray-600 mb-1';

const SpinTab: React.FC<SpinTabProps> = ({ spin, onSaveSpin, saving }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<SpinSettings>(spin);
  const [dirty, setDirty] = useState(false);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editing, setEditing] = useState<SpinPrizeItem | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<SpinPrizeItem | null>(
    null
  );
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    setForm(spin);
    setDirty(false);
  }, [spin]);

  const set = <K extends keyof SpinSettings>(k: K, v: SpinSettings[K]) => {
    setForm((p) => ({ ...p, [k]: v }));
    setDirty(true);
  };

  /* ── Prizes ── */
  const {
    data: prizes,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['spin-prizes'],
    queryFn: () => spinService.getAllPrizes(),
    staleTime: 60 * 1000,
  });

  const list = prizes || [];

  const handleAdd = () => {
    setEditing(null);
    setIsFormOpen(true);
  };

  const handleEdit = (prize: SpinPrizeItem) => {
    setEditing(prize);
    setIsFormOpen(true);
  };

  const handleFormSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['spin-prizes'] });
  };

  const handleToggleActive = async (prize: SpinPrizeItem) => {
    setBusyId(prize._id);
    try {
      await spinService.togglePrizeActive(prize._id);
      queryClient.invalidateQueries({ queryKey: ['spin-prizes'] });
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (prize: SpinPrizeItem) => {
    setBusyId(prize._id);
    try {
      await spinService.removePrize(prize._id);
      queryClient.invalidateQueries({ queryKey: ['spin-prizes'] });
      setDeleteConfirm(null);
    } finally {
      setBusyId(null);
    }
  };

  /* ── Type badge ── */
  const typeBadge = (t: SpinPrizeItem['type']) => {
    const map: Record<string, { bg: string; text: string; label: string }> = {
      money: {
        bg: 'bg-green-50',
        text: 'text-green-700',
        label: 'Money',
      },
      item: {
        bg: 'bg-blue-50',
        text: 'text-blue-700',
        label: 'Item',
      },
      nothing: {
        bg: 'bg-gray-100',
        text: 'text-gray-600',
        label: 'Nothing',
      },
      spin_again: {
        bg: 'bg-amber-50',
        text: 'text-amber-700',
        label: 'Spin Again',
      },
    };
    const m = map[t] || map.nothing;
    return (
      <span
        className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium uppercase ${m.bg} ${m.text}`}
      >
        {m.label}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      {/* Master settings */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">
              Spin to Win
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Configure the spin wheel, prize pool, and reward rules.
            </p>
          </div>
          <button
            onClick={() => navigate('/spin-wins')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white text-primary-600 border border-primary-200 hover:bg-primary-50"
          >
            <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
            View Spin Wins
          </button>
        </div>

        <div className="space-y-3">
          {/* Enabled toggle */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
            <div>
              <p className="text-sm font-medium text-gray-900">
                Spin Enabled
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                When off, users see a "service unavailable" screen instead
                of the wheel.
              </p>
            </div>
            <button
              onClick={() => set('enabled', !form.enabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                form.enabled ? 'bg-primary-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  form.enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Coin amount */}
          <div>
            <label className={labelClass}>Coins Per Grant</label>
            <input
              type="number"
              min={0}
              step={1}
              value={form.coinAmount}
              onChange={(e) =>
                set('coinAmount', Number(e.target.value) || 0)
              }
              disabled={!form.enabled}
              className={`${inputClass} ${
                !form.enabled ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
            />
            <p className="text-[10px] text-gray-500 mt-1">
              Coins awarded per reward event (welcome grant = 2×, deposit
              reward = 1×, 2× or 3× depending on amount).
            </p>
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <Button
            size="sm"
            disabled={!dirty || saving}
            onClick={() => onSaveSpin(form)}
          >
            {saving ? 'Saving...' : 'Save Spin Settings'}
          </Button>
        </div>
      </Card>

      {/* Prize table */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">
              Prizes
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Prizes shown on the wheel. Between 4 and 8 should be active.
            </p>
          </div>
          <Button size="sm" onClick={handleAdd}>
            <PlusIcon className="w-4 h-4 mr-1.5" />
            Add Prize
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-solid border-primary-500 border-t-transparent" />
          </div>
        ) : error ? (
          <div className="text-center py-6">
            <p className="text-sm text-red-500">
              {error instanceof Error
                ? error.message
                : 'Failed to load prizes'}
            </p>
            <button
              onClick={() => refetch()}
              className="mt-3 px-4 py-2 bg-primary-500 text-white rounded-lg text-sm hover:bg-primary-600"
            >
              Retry
            </button>
          </div>
        ) : list.length === 0 ? (
          <div className="text-center py-10">
            <GiftIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No prizes configured yet</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Add at least 4 prizes to enable the wheel.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-[10px] text-gray-500 uppercase tracking-wide border-b border-gray-100">
                  <th className="px-3 py-2 font-medium">Order</th>
                  <th className="px-3 py-2 font-medium">Label</th>
                  <th className="px-3 py-2 font-medium">Type</th>
                  <th className="px-3 py-2 font-medium">Amount</th>
                  <th className="px-3 py-2 font-medium">Probability</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.map((p) => (
                  <tr
                    key={p._id}
                    className="border-b border-gray-50 hover:bg-gray-50/60"
                  >
                    <td className="px-3 py-3 text-xs text-gray-500">
                      {p.sortOrder}
                    </td>
                    <td className="px-3 py-3">
                      <p className="text-xs font-medium text-gray-900">
                        {p.label}
                      </p>
                      {p.labelAlt && (
                        <p className="text-[10px] text-gray-400 mt-0.5">
                          {p.labelAlt}
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-3">{typeBadge(p.type)}</td>
                    <td className="px-3 py-3 text-xs text-gray-600">
                      {p.type === 'money' ? `ETB ${p.amount}` : '—'}
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-600">
                      {p.probability}
                    </td>
                    <td className="px-3 py-3">
                      {p.isActive ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-50 text-green-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600">
                          <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                          Disabled
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => handleToggleActive(p)}
                          disabled={busyId === p._id}
                          className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors disabled:opacity-50 ${
                            p.isActive
                              ? 'bg-red-50 text-red-600 hover:bg-red-100'
                              : 'bg-green-50 text-green-600 hover:bg-green-100'
                          }`}
                        >
                          {busyId === p._id
                            ? '...'
                            : p.isActive
                            ? 'Disable'
                            : 'Enable'}
                        </button>
                        <button
                          onClick={() => handleEdit(p)}
                          className="p-1.5 rounded-lg hover:bg-primary-50 text-primary-600 transition-colors"
                          title="Edit"
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(p)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition-colors"
                          title="Delete"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Prize form */}
      <SpinPrizeFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSuccess={handleFormSuccess}
        prize={editing}
      />

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setDeleteConfirm(null)}
          />
          <div className="relative bg-white rounded-xl shadow-xl max-w-sm w-full p-5">
            <h3 className="text-base font-semibold text-gray-900 mb-2">
              Delete "{deleteConfirm.label}"?
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              This cannot be undone. The prize will no longer appear on the
              wheel.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50"
                disabled={busyId === deleteConfirm._id}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                disabled={busyId === deleteConfirm._id}
              >
                {busyId === deleteConfirm._id ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpinTab;